import { isDseaEnabled, isGseaEnabled } from '@/modules/enrichment/helpers';
import type { ToolSection } from './types';

export const toHashLabel = (v: string) =>
  v
    .replace('v', '')
    .replace(/(\d{1,2})(\d*)/, (_: string, maj: string, min: string) => (min ? `${maj}.${min}` : maj))
    .replace(/\.0$/, '');

export const sections = [
  { label: 'DISGENET statistics', value: 'gdaStatistics', group: 'Statistics' },
  { label: 'Stats by Source', value: 'stats_by_source', group: 'Statistics' },
  { label: 'Schema', value: 'schema', group: 'Data' },
  { label: 'Sources', value: 'sources', group: 'Data' },
  { label: 'Metrics', value: 'metrics', group: 'Data' },
  { label: 'Attributes', value: 'attributes', group: 'Data' },
  { label: 'DISGENET Association Type Ontology', value: 'assoctypeont', group: 'Data' },
  { label: 'How to cite us', value: 'cite_us', group: 'How To Cite Us' },
];

export const toolsSections: ToolSection[] = [
  { group: 'API', label: 'Interactive Console', value: 'console' },
  { group: 'API', label: 'Endpoints', value: 'endpoints' },
  { group: 'API', label: 'Code examples', value: 'examples' },
  { group: 'API', label: 'Information and support', value: 'support' },
  { group: 'Cytoscape App', label: 'Installation', value: 'installcyto' },
  { group: 'DISGENET AI', label: 'AIDA', value: 'assistant', icon: 'aida' },
  { group: 'MCP Connector', label: 'How to use', value: 'mcp', icon: 'mcp' },
  { group: 'ENRICHMENT', label: 'ORA', value: 'enrichment' },
  ...(isDseaEnabled ? [{ group: 'ENRICHMENT', label: 'DSEA', value: 'dsea' } as ToolSection] : []),
  ...(isGseaEnabled ? [{ group: 'ENRICHMENT', label: 'GSEA', value: 'gsea' } as ToolSection] : []),
  { group: 'R Package', label: 'Installation', value: 'Rinstallation' },
  { group: 'R Package', label: 'Documentation', value: 'Rdocumentation' },
  {
    group: 'DISGENET download',
    label: 'How to download DISGENET',
    value: 'download',
    roles: ['ROLE_UNLIMITED', 'ROLE_DEVELOPER'],
  },
];

const ORDINALS = ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'];

export function formatVersionAsDate(versionOrDate: string): string {
  const trimmed = (versionOrDate || '').trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!isoMatch) return trimmed;
  const [, y, m, d] = isoMatch;
  const monthIndex = parseInt(m, 10) - 1;
  const day = parseInt(d, 10);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const suffix = day >= 10 && day <= 20 ? 'th' : ORDINALS[day % 10];
  return `${months[monthIndex]} ${day}${suffix} ${y}`;
}

export const formatVersion = (v: string): string => {
  const withoutV = v.replace(/^v/, '');

  if (/^\d{4}-\d{2}-\d{2}$/.test(withoutV)) {
    return formatVersionAsDate(withoutV);
  }

  if (/^\d{4}$/.test(withoutV)) {
    const major = parseInt(withoutV.slice(0, 2), 10);
    const minor = parseInt(withoutV.slice(2, 3), 10);
    const patch = parseInt(withoutV.slice(3), 10);
    return `${major}.${minor}.${patch}`;
  }

  if (/^\d{3}$/.test(withoutV)) {
    const major = parseInt(withoutV.slice(0, 2), 10);
    const minor = parseInt(withoutV.slice(2), 10);
    return `${major}.${minor}`;
  }

  if (/^\d{2}$/.test(withoutV)) {
    return `${parseInt(withoutV, 10)}`;
  }

  return withoutV;
};

/**
 * Parsea una cadena de versión (ej. "26.1", "25.1.1", "v253") a un array de números para comparar.
 */
function parseVersionParts(version: string): number[] {
  const normalized = version.replace(/^v/i, '').trim();
  if (/^\d{3}$/.test(normalized)) {
    return [parseInt(normalized.slice(0, 2), 10), parseInt(normalized.slice(2), 10)];
  }
  if (/^\d{4}$/.test(normalized)) {
    return [
      parseInt(normalized.slice(0, 2), 10),
      parseInt(normalized.slice(2, 3), 10),
      parseInt(normalized.slice(3), 10),
    ];
  }
  return normalized.split('.').map(n => parseInt(n, 10) || 0);
}

/**
 * Compara dos versiones. Devuelve > 0 si a es más reciente que b, < 0 si b es más reciente, 0 si igual.
 * Orden descendente (más reciente primero): sort((a, b) => compareVersions(b.version, a.version)).
 */
export function compareVersions(a: string, b: string): number {
  const partsA = parseVersionParts(a);
  const partsB = parseVersionParts(b);
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const numA = partsA[i] ?? 0;
    const numB = partsB[i] ?? 0;
    if (numA !== numB) return numA - numB;
  }
  return 0;
}

export const formatNumber = (n: unknown) => {
  if (typeof n === 'number' || typeof n === 'string') {
    return Number(n).toLocaleString();
  }
  return 'N/A';
};

export const isReleaseCategory = (category: string): boolean => {
  return category.includes('release');
};
